import { ZervantLocale } from '@features/invoices/enums/zervantLocale'

import { Metable } from '../../types/metable'
import apiZervant from '../../utils/apiZervant'
import { DownloadFormat } from './enums/downloadFormat'
import { FetchSalesOverviewReportParams, FetchSalesReportParams } from './types/fetchSalesReportParams'
import { SalesByCustomerReportItem } from './types/salesByCustomerReportItem'
import { SalesByItemReportItem } from './types/salesByItemReportItem'
import { SalesReportLocaleIdentifier } from './types/salesReportLocaleIdentifier'
import { SalesReportSummary } from './types/salesReportSummary'

// Sales overview

export interface FetchSalesReportResponseData extends Metable {
  data: SalesReportSummary[]
}

export const fetchSalesOverviewReport = async ({
  dateSelection,
  startDate,
  endDate,
}: FetchSalesOverviewReportParams) => {
  return apiZervant.documentReports.get<FetchSalesReportResponseData>(
    `/sales/${dateSelection}/${startDate}/${endDate}?paidOnly=false`,
  )
}

interface FetchSalesReportDownloadParams {
  endDate: string
  format: DownloadFormat
  locale?: SalesReportLocaleIdentifier
  startDate: string
}

interface FetchSalesOverviewReportDownloadParams extends FetchSalesReportDownloadParams {
  dateSelection: FetchSalesOverviewReportParams['dateSelection']
}

export interface FetchSalesReportDownloadDataResponse {
  data: Blob
}

export const fetchSalesOverviewReportDownloadFileData = async ({
  dateSelection,
  endDate,
  format,
  locale = ZervantLocale.EN_GB,
  startDate,
}: FetchSalesOverviewReportDownloadParams): Promise<FetchSalesReportDownloadDataResponse> => {
  return apiZervant.documentReports.get(
    `/sales/${dateSelection}/${startDate}/${endDate}?paidOnly=false&format=${format}&locale=${locale}`,
    { responseType: 'blob' },
  )
}

interface FetchSalesOverviewReportInvoicesDownloadArchiveDataParams {
  creditNoteIds: number[]
  invoiceIds: number[]
}

export const fetchSalesOverviewReportInvoicesDownloadArchiveData = async ({
  creditNoteIds,
  invoiceIds,
}: FetchSalesOverviewReportInvoicesDownloadArchiveDataParams): Promise<FetchSalesReportDownloadDataResponse> => {
  return apiZervant.restInvoice.get(
    `/invoices/archive?invoice-ids=${invoiceIds.join(',')}&creditnote-ids=${creditNoteIds.join(',')}`,
    { responseType: 'blob' },
  )
}

// Sales By Item

interface FetchSalesByItemReportResponseData extends Metable {
  data: SalesByItemReportItem[]
}

export const fetchSalesByItemReport = ({ startDate, endDate }: FetchSalesReportParams) => {
  return apiZervant.documentReports.get<FetchSalesByItemReportResponseData>(
    `/sales/product/issue/${startDate}/${endDate}?paidOnly=false`,
  )
}

export const fetchSalesByItemReportDownloadFileData = async ({
  endDate,
  format,
  locale = ZervantLocale.EN_GB,
  startDate,
}: FetchSalesReportDownloadParams): Promise<FetchSalesReportDownloadDataResponse> => {
  return apiZervant.documentReports.get(
    `/sales/product/issue/${startDate}/${endDate}?paidOnly=false&format=${format}&locale=${locale}`,
    { responseType: 'blob' },
  )
}

// Sales by Customer

export interface FetchSalesByCustomerReportResponseData extends Metable {
  data: SalesByCustomerReportItem[]
}

export const fetchSalesByCustomerReport = ({ startDate, endDate }: FetchSalesReportParams) => {
  return apiZervant.documentReports.get<FetchSalesByCustomerReportResponseData>(
    `/sales/customer/issue/${startDate}/${endDate}?paidOnly=false`,
  )
}

export const fetchSalesByCustomerReportDownloadFileData = async ({
  endDate,
  format,
  locale = ZervantLocale.EN_GB,
  startDate,
}: FetchSalesReportDownloadParams): Promise<FetchSalesReportDownloadDataResponse> => {
  return apiZervant.documentReports.get(
    `/sales/customer/issue/${startDate}/${endDate}?paidOnly=false&format=${format}&locale=${locale}`,
    { responseType: 'blob' },
  )
}
